import { useMemo } from 'react';
import { useGetCatalogQuery, useRecordByCodeQuery, useSearchQuery } from '@platform/state/api';
import type { ISearchView } from './ISearchView';
import { mergeCodeHit } from './mergeCodeHit';
import { parseCodeQuery } from './parseCodeQuery';
import { useDebounced } from './useDebounced';

/** Скільки збігів групи просити (прототип v4: перші 20; кількість групи сервер повертає повну). */
const PER_GROUP = 20;
/** Пауза набору перед запитом, мс. */
const TYPING_MS = 120;

/**
 * Пошук записів робочого простору під час набору (patterns/search): від двох символів, із затримкою 120 мс (прототип шукає на кожен символ,
 * затримка лише об'єднує швидкий набір в один запит) — повнотекстовий пошук сервера плюс запис за кодом, як його показано («TSK-3», «tsk3»):
 * префікс → тип запису каталогу, номер → фільтр code:eq. Групи — за типами записів (прототип v4 групує за ws.modules: «Завдання»,
 * «Підготовчі завдання»).
 */
export function useRecordSearch(ws: string, text: string): ISearchView {
  const query = useDebounced(text.trim(), TYPING_MS);
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const code = parseCodeQuery(query);
  const rt = code ? catalog?.modules.flatMap((m) => m.recordTypes).find((r) => r.prefix.toUpperCase() === code.prefix)?.key : undefined;
  const ready = query.length >= 2;
  const search = useSearchQuery({ q: query, workspaceId: ws, perGroup: PER_GROUP, groupBy: 'recordType' }, { skip: !ready });
  const byCode = useRecordByCodeQuery({ ws, rt: rt ?? '', code: code?.code ?? 0 }, { skip: !ready || !rt });
  const result = useMemo(() => (search.data ? mergeCodeHit(search.data, rt ? byCode.data : null, ws) : undefined), [search.data, byCode.data, rt, ws]);
  return { query: ready ? query : '', result: ready ? result : undefined, fetching: search.isFetching || byCode.isFetching };
}
